import clsx from 'clsx';
import { type ReactNode, useLayoutEffect, useRef, useState } from 'react';

/**
 * Широкая таблица на узком экране: прокрутка вбок с затенением края, пока справа или слева есть
 * столбцы, и закреплённым первым столбцом на телефоне — видно, что таблица продолжается и к какой
 * строке относятся цифры.
 */
export function ScrollX({
  children,
  className,
  stickyFirst = true,
}: {
  children: ReactNode;
  className?: string;
  /** Закрепить первый столбец на телефоне; не для таблиц, которые на телефоне — карточки */
  stickyFirst?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [edge, setEdge] = useState({ left: false, right: false, wide: false });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const upd = () =>
      setEdge((prev) => {
        const next = {
          left: el.scrollLeft > 2,
          right: el.scrollLeft + el.clientWidth < el.scrollWidth - 2,
          wide: el.scrollWidth > el.clientWidth + 2,
        };
        return prev.left === next.left && prev.right === next.right && prev.wide === next.wide ? prev : next;
      });
    upd();
    el.addEventListener('scroll', upd, { passive: true });
    const ro = new ResizeObserver(upd);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    return () => {
      el.removeEventListener('scroll', upd);
      ro.disconnect();
    };
  }, []);
  return (
    <div className={clsx('scroll-x', stickyFirst && 'sticky-first', className)}>
      <div className="relative">
        <div ref={ref} className="overflow-x-auto">
          {children}
        </div>
        <div
          aria-hidden
          className={clsx(
            'pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-surface to-transparent transition-opacity',
            edge.right ? 'opacity-100' : 'opacity-0',
          )}
        />
      </div>
      {/* Подсказка под таблицей, а не поверх: не закрывает заголовки столбцов */}
      {edge.wide ? (
        <p aria-hidden className="border-t border-line px-4 py-1.5 text-[12px] text-ink-2 md:hidden">
          Таблица шире экрана — сдвиньте её влево, чтобы увидеть остальные столбцы
        </p>
      ) : null}
    </div>
  );
}
